import { Checkbox, FormControlLabel, Grid, IconButton, LinearProgress, TextField, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageTitle from '../../../components/page-title/PageTitle';
import IPage from '../../../interfaces/IPage';
import { Container, Form } from './PointSituationForm.styled';

import { MdOutlineAssignmentReturn } from 'react-icons/md';
import { useToastrVanila } from '../../../hooks/util/useToastrVanila';
import ITypeDocument from '../../../interfaces/ITypeDocument';
import IFormError from '../../../interfaces/IFormError';
import { useValidateSchema } from '../../../hooks/providers/useValidateSchema';
import * as yup from 'yup';
import { LoadingButton } from '@mui/lab';
import { usePointSituation } from '../../../hooks/services/usePointSituatuion';
import IPointSituation from '../../../interfaces/IPointSituation';

interface DocumentTypeFormProps extends IPage {}

const initialPointSituation: IPointSituation = {
  description: '',
  allowsChange: false,
};

const PointSituationForm = (props: DocumentTypeFormProps) => {
  window.document.title = props.title;
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState<'edit' | 'new'>('edit');
  const [id, setId] = useState('');
  const [pointSituation, setPointSituation] = useState<IPointSituation>({ ...initialPointSituation });
  const [errors, setErrors] = useState<IFormError | null>(null);
  const [actionLabel, setActionLabel] = useState('INCLUSÃO');

  const navigate = useNavigate();
  const location = useLocation();
  const request = usePointSituation();
  const toastr = useToastrVanila();
  const validation = useValidateSchema();

  const schema = yup.object({
    description: yup.string().required('Campo obrigatório'),
    order: yup.number().min(1, 'Campo obrigatório'),
  });

  useEffect(() => {
    const action = location.pathname.split('/')[location.pathname.split('/').length - 1] as 'edit' | 'new';
    const id = location.pathname.split('/')[location.pathname.split('/').length - 2];
    setAction(action);
    if (action === 'edit') {
      setActionLabel('EDIÇÃO');
    } else {
      setActionLabel('INCLUSÃO');
    }
    setId(id);
    if (id !== '') {
      listPointSituationById(id);
    }
  }, []);

  const listPointSituationById = async (id: string): Promise<void> => {
    setLoading(true);
    await request
      .listById(id)
      .then(result => {
        setLoading(false);
        setPointSituation({ ...result });
      })
      .catch(error => {
        setLoading(false);
        toastr.error(error.message);
      });
  };

  const createPointSituation = async (typeDocument: ITypeDocument): Promise<void> => {
    setLoading(true);
    await request
      .create({ ...typeDocument })
      .then(result => {
        setLoading(false);
        setPointSituation({ ...result });
        toastr.success('Documento cadastrado com sucesso!');
        navigate(`/point-situation`);
      })
      .catch(error => {
        setLoading(false);
      });
  };

  const updatePointSituation = async (id: string, typeDocument: ITypeDocument): Promise<void> => {
    setLoading(true);
    await request
      .update(id, { ...typeDocument })
      .then(result => {
        setLoading(false);
        setPointSituation({ ...result });
        toastr.success('Documento atualizado com sucesso!');
        navigate(`/point-situation`);
      })
      .catch(error => {
        toastr.error(`Erro ao atualizar os dados: ${error.message}`);
        setLoading(false);
      });
  };

  const handleCancel = async () => {
    if (action === 'edit') {
      listPointSituationById(id);
    } else {
      setPointSituation({ ...initialPointSituation });
    }
  };

  const handleSaveClick = async () => {
    const result = await validation.validate(schema, pointSituation);
    setErrors(result.errors);
    if (!result.isValid) {
      return;
    }
    if (action === 'edit') {
      await updatePointSituation(id, pointSituation);
    } else {
      await createPointSituation(pointSituation);
    }
  };

  return (
    <Container>
      <PageTitle title={`${actionLabel} SITUAÇÃO DO PONTO`}>
        <Tooltip title="Voltar para listagem" placement="left-start">
          <IconButton onClick={() => navigate(`/point-situation`)} color="inherit">
            <MdOutlineAssignmentReturn />
          </IconButton>
        </Tooltip>
      </PageTitle>
      {loading && <LinearProgress />}
      <Form>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Descrição"
              placeholder="Informe a descrição do documento"
              value={pointSituation?.description}
              onChange={e => setPointSituation({ ...pointSituation, description: e.target.value })}
              error={Boolean(errors?.description)}
              helperText={errors?.description}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              label="Permitir Mudança"
              control={
                <Checkbox
                  onChange={e => setPointSituation({ ...pointSituation, allowsChange: e.target.checked })}
                  checked={pointSituation.allowsChange}
                />
              }
            />
          </Grid>
        </Grid>
      </Form>
      <Grid container direction="row" justifyContent="flex-start" alignItems="center" sx={{ marginTop: 5 }} spacing={2}>
        <Grid item>
          <LoadingButton loading={loading} variant="contained" color="secondary" onClick={handleCancel}>
            Cancelar
          </LoadingButton>
        </Grid>
        <Grid item>
          <LoadingButton loading={loading} variant="contained" color="primary" onClick={handleSaveClick}>
            Salvar
          </LoadingButton>
        </Grid>
      </Grid>
    </Container>
  );
};

export default PointSituationForm;
